/**
 * RadioHandler.ts
 * Radio playback + now-playing metadata helpers (web-first).
 *
 * - Live Radio: station list + stream playback
 * - Octave Radio: fixed stream URL + metadata API polling
 *
 * NOTE:
 * - Web playback uses the existing ClientTransportHandler + WebAudioRenderer pipeline
 *   (trackChanged/playbackStateChanged events drive the mini player and audio element).
 * - iOS/Android native apps will eventually use native pickers and native streaming;
 *   this module is an explicit seam for that work.
 */
import { getClientTransport } from './ClientTransportHandler';
import { useMaestroStore } from '../store/maestroStore';
import { TransportCommand } from '../transport/TransportCommand';
import { QueueController } from '../transport/QueueController';
import { TransportState } from '../transport/TransportState';
import { clearRadioPauseHold, pauseLiveRadio } from '../radio/radioPauseHold';

export type RadioStation = {
  id: string;
  name: string;
  streamUrl: string;
  subtitle?: string;
};

export type NowPlaying = {
  title: string;
  artist: string;
  startedAtMs?: number;
  endsAtMs?: number;
};

const OCTAVE_STREAM_URL = 'https://octaverecords.out.airtime.pro/octaverecords_a';
const OCTAVE_META_V2 = 'https://octaverecords.airtime.pro/api/live-info-v2';
const OCTAVE_META_FALLBACK = 'https://octaverecords.airtime.pro/api/live-info';
export const RADIO_ARTWORK_URL = 'https://maestrodesktop.com/icons/radio-live.png';

function parseAirtimeTime(s?: string): number | null {
  // API returns "YYYY-MM-DD HH:mm:ss" in station timezone.
  // We treat it as local time; for display/progress this is good enough.
  if (!s) return null;
  const t = String(s).trim();
  if (!t) return null;
  // Convert "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ss"
  const isoish = t.replace(' ', 'T');
  const ms = Date.parse(isoish);
  return Number.isFinite(ms) ? ms : null;
}

export class RadioHandler {
  private octavePollTimer: ReturnType<typeof setInterval> | null = null;

  getOctaveStreamUrl(): string {
    return OCTAVE_STREAM_URL;
  }

  async fetchOctaveNowPlaying(): Promise<NowPlaying | null> {
    const tryFetch = async (url: string) => {
      // Airtime endpoints can be cached aggressively by browsers/CDNs; force a fresh read.
      const u = `${url}${url.includes('?') ? '&' : '?'}_cb=${Date.now()}`;
      const res = await fetch(u, {
        method: 'GET',
        cache: 'no-store',
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) return null;
      const data = await res.json().catch(() => null);
      return data;
    };

    const data =
      (await tryFetch(OCTAVE_META_V2)) ||
      (await tryFetch(OCTAVE_META_FALLBACK));

    const current = data?.tracks?.current;
    if (!current) return null;

    const title = String(current?.metadata?.track_title || '').trim() || String(current?.name || '').split(' - ').slice(1).join(' - ').trim();
    const artist = String(current?.metadata?.artist_name || '').trim() || String(current?.name || '').split(' - ')[0]?.trim() || 'Octave Radio';

    const startedAtMs = parseAirtimeTime(current?.starts) || undefined;
    const endsAtMs = parseAirtimeTime(current?.ends) || undefined;

    return { title: title || 'Octave Radio', artist, startedAtMs, endsAtMs };
  }

  async playStream(opts: {
    stationId: string;
    stationName: string;
    streamUrl: string;
    nowPlaying?: NowPlaying | null;
    artworkUrl?: string;
  }): Promise<void> {
    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;

    const np = opts.nowPlaying || null;
    const title = String(np?.title || opts.stationName || 'Radio').trim();
    const artist = String(np?.artist || 'Live').trim();
    const artworkUrl = String(opts.artworkUrl || RADIO_ARTWORK_URL).trim();

    const startedAtMs = np?.startedAtMs || null;
    const endsAtMs = np?.endsAtMs || null;
    const duration =
      startedAtMs && endsAtMs && endsAtMs > startedAtMs
        ? Math.max(0, Math.floor((endsAtMs - startedAtMs) / 1000))
        : 0;

    const trackData = {
      id: String(opts.stationId),
      title,
      artist,
      album: 'Radio',
      duration,
      artworkUrl,
      source: 'radio',
      stationName: String(opts.stationName || 'Radio'),
    };

    clearRadioPauseHold();
    QueueController.stopForOwnershipTransfer();
    TransportState.claimTransportOwner('radio');

    // Update UI immediately (mini player appears), even before server echoes state.
    store.setActiveSource('radio');
    store.setCurrentTrack(trackData as any);
    store.setPlaybackState('playing');
    if (duration > 0 && startedAtMs) {
      const pos = Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000));
      store.updateTime(pos, duration);
    } else {
      store.updateTime(0, 0);
    }

    if (renderer) {
      const formatId = 7;
      console.log('[RadioHandler] routing live radio to renderer:', renderer.name, 'formatId:', formatId);
      await TransportCommand.stopRenderer(renderer.id, { caller: 'radio_start', userInitiated: true }).catch(() => {});
      await TransportCommand.playUri(String(opts.streamUrl), renderer.id, formatId, trackData).catch((err: any) => {
        console.error('[RadioHandler] renderer playUri failed:', err);
      });
    } else {
      // Local playback — use TransportCommand.playUri with empty rendererId.
      // Falls through to the shared WebAudioRenderer element (gesture-activated).
      console.log('[RadioHandler] routing live radio to local via TransportCommand');
      await TransportCommand.playUri(String(opts.streamUrl), '', 0, trackData).catch((err: any) => {
        console.error('[RadioHandler] local playUri failed, falling back to transport.play');
        const transport = getClientTransport();
        transport.play({
          trackId: String(opts.stationId),
          streamUrl: String(opts.streamUrl),
          title,
          artist,
          album: 'Radio',
          artworkUrl,
          duration,
          position: store.position,
        }).catch(() => {});
      });
    }
  }

  async pause(): Promise<void> {
    await pauseLiveRadio();
  }

  startOctavePolling(onUpdate: (np: NowPlaying | null) => void, intervalMs = 12000): void {
    this.stopOctavePolling();
    const tick = async () => {
      const np = await this.fetchOctaveNowPlaying().catch(() => null);
      onUpdate(np);

      // Keep the mini player in sync while Octave is playing.
      try {
        const store = useMaestroStore.getState();
        const ct: any = (store as any)?.currentTrack || null;
        const playingOctave = String(ct?.id || '') === 'octave_radio';
        if (!playingOctave) return;
        if (!np) return;
        const title = String(np?.title || 'Octave Radio').trim();
        const artist = String(np?.artist || 'Live').trim();
        const startedAtMs = np?.startedAtMs || null;
        const endsAtMs = np?.endsAtMs || null;
        const duration =
          startedAtMs && endsAtMs && endsAtMs > startedAtMs
            ? Math.max(0, Math.floor((endsAtMs - startedAtMs) / 1000))
            : (ct?.duration || 0);
        (store as any).setCurrentTrack?.({
          ...(ct || {}),
          id: 'octave_radio',
          title,
          artist,
          duration,
          artworkUrl: String((ct as any)?.artworkUrl || RADIO_ARTWORK_URL).trim(),
          source: 'radio',
          album: 'Radio',
        });
        if (duration > 0 && startedAtMs) {
          const pos = Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000));
          (store as any).updateTime?.(pos, duration);
        }
      } catch {
        // ignore
      }
    };
    tick();
    this.octavePollTimer = setInterval(tick, intervalMs);
  }

  stopOctavePolling(): void {
    if (this.octavePollTimer) {
      clearInterval(this.octavePollTimer);
      this.octavePollTimer = null;
    }
  }

  getDefaultLiveStations(): RadioStation[] {
    // Placeholder list (we'll replace with a real local-stations + favorites pipeline).
    return [
      { id: 'local_station_1', name: 'Local Station 1', subtitle: 'Nearby • FM simulcast', streamUrl: 'https://octaverecords.out.airtime.pro/octaverecords_a' },
      { id: 'local_station_2', name: 'Local Station 2', subtitle: 'Community • HD', streamUrl: 'https://octaverecords.out.airtime.pro/octaverecords_a' },
      { id: 'signed_station_1', name: 'Signed-in Station', subtitle: 'Favorites (coming soon)', streamUrl: 'https://octaverecords.out.airtime.pro/octaverecords_a' },
    ];
  }
}

let __radio: RadioHandler | null = null;
export function getRadioHandler(): RadioHandler {
  if (!__radio) __radio = new RadioHandler();
  return __radio;
}

